import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { baseUrl } from '../../../environments/environment';
import { StorageService } from './storage.service';
import { ChartDrawingApi } from '../chart.interface';

/** API response when backend returns { success, data: { chartDrawings } } */
export interface GetChartDrawingsResponse {
  success?: boolean;
  data?: { chartDrawings?: ChartDrawingApi[] };
  chartDrawings?: ChartDrawingApi[];
}

@Injectable({
  providedIn: 'root'
})
export class ChartDrawingsService {

  constructor(
    private http: HttpClient,
    private storage: StorageService
  ) {}

  /**
   * GET /chart/get-chart-drawings - Fetch saved drawings for symbol + timeRange + interval.
   * @param timeRange Timeframe (e.g. 1y, 1m, 5d)
   * @param interval Candle interval (e.g. 1d, 1h)
   */
  getChartDrawings(symbol: string, timeRange: string, interval: string, disableHttpLoader = 'true'): Observable<ChartDrawingApi[]> {
    const params = new HttpParams()
      .set('symbol', symbol)
      .set('timeframe', timeRange)
      .set('interval', interval);
    return this.http.get<GetChartDrawingsResponse | ChartDrawingApi[]>(`${baseUrl}/chart/get-chart-drawings`, {
      params,
      headers: new HttpHeaders()
        .set('Authorization', 'Bearer ' + (this.storage.get('token') || ''))
        .set('disableHttpLoader', disableHttpLoader),
    }).pipe(
      map((res): ChartDrawingApi[] => {
        if (Array.isArray(res)) return res;
        const wrapped = res as GetChartDrawingsResponse;
        return wrapped?.data?.chartDrawings ?? wrapped?.chartDrawings ?? [];
      })
    );
  }

  /**
   * POST /chart/update-chart-drawings - Save/update drawings for symbol + timeRange + interval.
   * @param timeRange Timeframe (e.g. 1y, 1m, 5d)
   * @param interval Candle interval (e.g. 1d, 1h)
   */
  updateChartDrawings(symbol: string, timeRange: string, interval: string, drawings: ChartDrawingApi[], disableHttpLoader = 'true'): Observable<any> {
    const body = { symbol, timeframe: timeRange, interval, drawings };
    return this.http.post(`${baseUrl}/chart/update-chart-drawings`, body, {
      headers: new HttpHeaders()
        .set('Authorization', 'Bearer ' + (this.storage.get('token') || ''))
        .set('disableHttpLoader', disableHttpLoader),
    });
  }
}
